import React from 'react';

import { customerDisplayName } from './customerName';
import { listTokens } from './listTokens';

// THE customer column for every list in Lavoro quotidiano (Clienti and Acquisti).
//
// Those nine pages each describe a different thing — a membership, a package, a video, a
// plan, a wallet — but they all answer "for which client?" first, and each page used to
// answer it differently: three labels for the same idea (GENERAL.clientName,
// GENERAL.username, GENERAL.customer) and the email sometimes as its own column, sometimes
// inline, sometimes missing. A provider scanning two tabs in a row had to re-learn the table.
//
// One shape, everywhere (owner, 2026-09-20): name on top, email underneath, sourced from the
// same customer fields. The email stops being a column of its own — it is part of the
// identity, not a separate datum — which frees a column and keeps the page's own subject
// (the membership, the package…) in the second position where it belongs.
//
// The email is still exported: `customerColumn()` declares it as a second CSV field via
// `exportColumns`, so a provider exporting a list for a mailing loses nothing.
// A row is not always one person. A video assigned to a customer GROUP has many buyers, and
// the old page reduced them to a bare number in a column of its own — the one list in these
// two sections where "for which client?" had no answer you could read. Pass `customers` and
// the cell keeps the same two-line rhythm: who it is, or how many, with the names underneath.
const CustomerCell = ({
  name,
  email,
  customers,
  summaryLabel,
  onOpen,
  openTitle,
  hideEmail = false,
  fallback = '—',
  maxNames = 3,
  alwaysShowSummary = false,
}) => {
  if (Array.isArray(customers)) {
    const people = customers.filter(Boolean);

    if (people.length === 1 && !alwaysShowSummary) {
      return <CustomerCell name={customerDisplayName(people[0])} email={people[0].email} hideEmail={hideEmail} fallback={fallback} />;
    }

    // Nome cliente (else the email before "@"), never the person's own username (`name`).
    const names = people
      .map(person => customerDisplayName(person))
      .filter(Boolean)
      .slice(0, maxNames);
    const rest = people.length - names.length;

    // "4 clienti" is a count, not an answer — the names are the answer, and the full list
    // belongs a click away rather than in a row that has to stay one line tall.
    const heading = people.length === 0 ? fallback : summaryLabel || String(people.length);

    return (
      <div style={{ minWidth: 0 }}>
        {onOpen && people.length > 0 ? (
          <button
            type="button"
            onClick={event => {
              event.stopPropagation();
              onOpen();
            }}
            title={openTitle}
            style={{
              all: 'unset',
              cursor: 'pointer',
              color: listTokens.primary,
              fontSize: 13.5,
              fontWeight: 600,
              whiteSpace: 'nowrap',
              textDecoration: 'underline',
              textDecorationStyle: 'dotted',
              textUnderlineOffset: 2,
            }}
          >
            {heading}
          </button>
        ) : (
          <div style={{ color: listTokens.textPrimary, fontSize: 13.5, fontWeight: 600, whiteSpace: 'nowrap' }}>{heading}</div>
        )}
        {names.length > 0 && (
          <div
            style={{
              color: listTokens.textMuted,
              fontSize: 12,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={people
              .map(person => person.name)
              .filter(Boolean)
              .join(', ')}
          >
            {names.join(' · ')}
            {rest > 0 ? ` +${rest}` : ''}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ minWidth: 0 }}>
      <div
        style={{
          color: listTokens.textPrimary,
          fontSize: 13.5,
          fontWeight: 600,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {name || fallback}
      </div>
      {!hideEmail && !!email && (
        <div
          style={{
            color: listTokens.textMuted,
            fontSize: 12,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={email}
        >
          {email}
        </div>
      )}
    </div>
  );
};

// The matching column descriptor, so no page re-invents the label or forgets the export.
// `name`/`email` are row accessors; `hideEmail` covers the pages that withhold emails from
// collaborators. Keeps the CSV two-field even though the table shows one column.
// `label` overrides the heading where a page asks the question differently (Acquisti: "A chi").
export const customerColumn = (t, { name, email, hideEmail = false, width, label = t('GENERAL.customer') } = {}) => ({
  label,
  width,
  exportColumns: [
    { label, value: row => (typeof name === 'function' ? name(row) : '') || '' },
    ...(hideEmail ? [] : [{ label: t('GENERAL.email'), value: row => (typeof email === 'function' ? email(row) : '') || '' }]),
  ],
});

export default CustomerCell;
